import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, TextField, MenuItem, Select, FormControl, InputLabel,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Chip, InputAdornment, Button, TablePagination,
    Drawer, IconButton, Avatar, Divider, Stack, CircularProgress,
} from '@mui/material';
import {
    Search as SearchIcon,
    CalendarToday as CalendarIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Close as CloseIcon,
    Visibility as ViewIcon,
    FileDownload as ExportIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { axiosInstance, postData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';

const ACTION_TYPES = ['All', 'Submitted', 'L1 Approved', 'Final Approved', 'Rejected'];

const ACTION_DISPLAY = {
    'L1 Approved':    'Final Approved',
    'Final Approved': 'External Approved',
};

const ACTION_STYLES = {
    Submitted:        { bg: '#eff6ff', color: '#3b82f6', border: '#93c5fd' },
    'L1 Approved':    { bg: '#ecfdf5', color: '#059669', border: '#6ee7b7' },
    'Final Approved': { bg: '#f0fdf4', color: '#15803d', border: '#86efac' },
    Rejected:         { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
};

function ActionChip({ action }) {
    const s = ACTION_STYLES[action] || { bg: '#f1f5f9', color: '#64748b', border: '#cbd5e1' };
    const label = ACTION_DISPLAY[action] || action;
    return (
        <Chip label={label} size="small"
            sx={{ bgcolor: s.bg, color: s.color, border: `1px solid ${s.border}`, fontWeight: 700, fontSize: 11 }} />
    );
}

function LevelChip({ level }) {
    const isEmployee = level === 'Employee';
    return (
        <Chip label={level} size="small"
            sx={{ bgcolor: isEmployee ? '#f1f5f9' : '#eef2ff', color: isEmployee ? '#64748b' : '#6366f1', fontWeight: 600, fontSize: 11 }} />
    );
}

function AuditDetailDrawer({ open, onClose, log }) {
    if (!log) return null;
    return (
        <Drawer anchor="right" open={open} onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 0 } }}>
            <Box sx={{ px: 3, py: 2.5, background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>Audit Log Detail</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, mt: 0.25 }}>{log.timestamp}</Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
            </Box>

            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <Avatar sx={{ bgcolor: '#6366f1', fontWeight: 700 }}>{log.employeeAvatar}</Avatar>
                    <Box>
                        <Typography sx={{ fontWeight: 700, color: '#1e293b' }}>{log.employee}</Typography>
                        <Typography sx={{ fontSize: 12, color: '#64748b' }}>{log.dept}</Typography>
                    </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {[
                    { label: 'Action',           value: <ActionChip action={log.action} /> },
                    { label: 'Performed By',      value: log.performedBy },
                    { label: 'Approval Level',    value: <LevelChip level={log.level} /> },
                    { label: 'Timesheet Period',  value: log.period },
                    { label: 'Timestamp',         value: log.timestamp },
                ].map(row => (
                    <Box key={row.label} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#64748b', width: 140, flexShrink: 0 }}>{row.label}</Typography>
                        <Box sx={{ flex: 1 }}>
                            {typeof row.value === 'string'
                                ? <Typography sx={{ fontSize: 13, color: '#1e293b' }}>{row.value}</Typography>
                                : row.value
                            }
                        </Box>
                    </Box>
                ))}

                {log.comment && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#fef9c3', borderRadius: 2, border: '1px solid #fde047' }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#854d0e', mb: 0.5 }}>Comment</Typography>
                        <Typography sx={{ fontSize: 13, color: '#1e293b' }}>{log.comment}</Typography>
                    </Box>
                )}
            </Box>
        </Drawer>
    );
}

const getLevelFromAction = (action) => {
    if (action === 'Submitted')      return 'Employee';
    if (action === 'L1 Approved')    return 'Manager';
    if (action === 'Final Approved') return 'External Approver';
    if (action === 'Rejected')       return 'Approver';
    return 'Employee';
};

const getAvatar = (name) =>
    (name ?? '?').split(' ').map(n => n[0] ?? '').join('').toUpperCase().slice(0, 2);

const formatTimestamp = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${d.toISOString().split('T')[0]} ${d.toTimeString().slice(0, 5)}`;
};

export default function TimesheetAuditLogs({ refreshKey }) {
    const [logs,         setLogs]         = useState([]);
    const [total,        setTotal]        = useState(0);
    const [loading,      setLoading]      = useState(false);
    const [search,       setSearch]       = useState('');
    const [actionFilter, setActionFilter] = useState('All');
    const [fromDate,     setFromDate]     = useState('');
    const [toDate,       setToDate]       = useState('');
    const [page,         setPage]         = useState(0);
    const [rowsPerPage,  setRowsPerPage]  = useState(10);
    const [drawerOpen,   setDrawerOpen]   = useState(false);
    const [selectedLog,  setSelectedLog]  = useState(null);
    const [isExporting, setIsExporting] = useState(false)

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(
                API_ENDPOINTS.getAuditLogs(
                    fromDate,
                    toDate,
                    actionFilter !== 'All' ? actionFilter : '',
                    '',
                    '',
                    page + 1,
                    rowsPerPage,
                )
            );
            const { data = [], total: t = 0 } = response.data?.result ?? {};
            setLogs(data.map((item, idx) => ({
                id:            `${item.timesheetId}-${item.action}-${idx}`,
                timestamp:     formatTimestamp(item.timestamp),
                employee:      item.employeeName ?? '',
                employeeAvatar: getAvatar(item.employeeName),
                dept:          item.employeeDept || '—',
                action:        item.action,
                performedBy:   item.actor ?? item.employeeName ?? '',
                period:        `${item.weekStart ?? ''} → ${item.weekEnd ?? ''}`,
                level:         getLevelFromAction(item.action),
                comment:       item.comment ?? '',
            })));
            setTotal(t);
        } catch (err) {
            console.error('Error fetching audit logs:', err);
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate, actionFilter, page, rowsPerPage]);

    useEffect(() => { fetchLogs(); }, [fetchLogs, refreshKey]);

    // Client-side text search on current page
    const filtered = logs.filter(log => {
        if (!search) return true;
        return log.employee.toLowerCase().includes(search.toLowerCase())
            || log.performedBy.toLowerCase().includes(search.toLowerCase());
    });

    const handleExport = async () => {
        try {
            setIsExporting(true);
            const response = await axiosInstance.post(
                API_ENDPOINTS.exportHRAuditLogs(),
                { from: fromDate, to: toDate, action: actionFilter, name: search }, { responseType: 'blob' }
            );
            const url = URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = `timesheet-audit-logs-${new Date().toISOString().split('T')[0]}.xlsx`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Box>
            {/* Filters */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2.5, p: 2.5, bgcolor: '#fff', borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <TextField placeholder="Search employee or actor..." size="small" value={search}
                    onChange={e => setSearch(e.target.value)}
                    sx={{ flex: '1 1 200px', minWidth: 200 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }} />

                <FormControl size="small" sx={{ flex: '1 1 150px', minWidth: 150 }}>
                    <InputLabel>Action Type</InputLabel>
                    <Select value={actionFilter} label="Action Type"
                        onChange={e => { setActionFilter(e.target.value); setPage(0); }}
                        startAdornment={<FilterIcon sx={{ fontSize: 16, mr: 0.5, color: '#94a3b8' }} />}>
                        {ACTION_TYPES.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                    </Select>
                </FormControl>

                <TextField label="From Date" type="date" size="small" value={fromDate}
                    onChange={e => { setFromDate(e.target.value); setPage(0); }}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><CalendarIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }}
                    sx={{ flex: '1 1 160px', minWidth: 160 }} />

                <TextField label="To Date" type="date" size="small" value={toDate}
                    onChange={e => { setToDate(e.target.value); setPage(0); }}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><CalendarIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }}
                    sx={{ flex: '1 1 160px', minWidth: 160 }} />

                <Stack direction="row" spacing={1} sx={{ ml: 'auto', alignItems: 'center' }}>
                    <Button size="small" startIcon={<RefreshIcon />}
                        onClick={() => { setSearch(''); setActionFilter('All'); setFromDate(''); setToDate(''); setPage(0); }}
                        sx={{ color: '#64748b', borderColor: '#e2e8f0', fontWeight: 600 }} variant="outlined">
                        Reset
                    </Button>
                    <Button size="small" startIcon={<ExportIcon />} onClick={handleExport} disabled={isExporting}
                        sx={{ bgcolor: '#6366f1', color: '#fff', fontWeight: 600, '&:hover': { bgcolor: '#4f46e5' } }} variant="contained">
                        Export
                    </Button>
                </Stack>
            </Box>

            <Typography sx={{ fontSize: 13, color: '#64748b', mb: 1.5 }}>
                <strong>{total}</strong> total audit event{total !== 1 ? 's' : ''}
                {search && <> · <strong>{filtered.length}</strong> match search</>}
            </Typography>

            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress sx={{ color: '#6366f1' }} />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Timestamp', 'Employee', 'Action', 'Performed By', 'Timesheet Period', 'Level', 'Comment', 'Details'].map(h => (
                                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, color: '#475569', py: 1.5, whiteSpace: 'nowrap' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filtered.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                                            <Typography sx={{ color: '#94a3b8', fontSize: 14 }}>No audit events found.</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filtered.map(log => (
                                        <TableRow key={log.id} sx={{ '&:hover': { bgcolor: '#f8fafc' }, '&:last-child td': { border: 0 } }}>
                                            <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap', color: '#475569' }}>{log.timestamp}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#6366f1', fontSize: 11, fontWeight: 700 }}>{log.employeeAvatar}</Avatar>
                                                    <Box>
                                                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{log.employee}</Typography>
                                                        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{log.dept}</Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell><ActionChip action={log.action} /></TableCell>
                                            <TableCell>
                                                <Typography sx={{ fontSize: 12, color: '#475569', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {log.performedBy}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography sx={{ fontSize: 12, color: '#475569', whiteSpace: 'nowrap' }}>{log.period}</Typography>
                                            </TableCell>
                                            <TableCell><LevelChip level={log.level} /></TableCell>
                                            <TableCell>
                                                <Typography sx={{ fontSize: 12, color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {log.comment || '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton size="small"
                                                    onClick={() => { setSelectedLog(log); setDrawerOpen(true); }}
                                                    sx={{ color: '#6366f1', '&:hover': { bgcolor: '#eef2ff' } }}>
                                                    <ViewIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <TablePagination component="div" count={total} page={page}
                    onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50]}
                    sx={{ borderTop: '1px solid #e2e8f0' }} />
            </Paper>

            <AuditDetailDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} log={selectedLog} />
        </Box>
    );
}
